import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  Stack,
  styled,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemText,
} from "@mui/material";
import {
  Info as InfoIcon,
  Sort as SortIcon,
} from "@mui/icons-material";
import DoubleRateCard from "./DoubleRateCard";
import SingleRateCard from "./SingleRateCard";
import InternationalRateCard from "./InternationalRateCard";
import SaveTripDrawer from "./SaveTripDrawer";
import SortAdBanner from "./SortAdBanner";
import { useSearchContext } from "../context/SearchContext";
import { sortFlights } from "../utils/sortUtils";
import { calculateBagPrices, formatPrice, getCityByAirportCode } from "../utils/flightDataUtils";

const TabContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  background: "#fff",
  borderRadius: 14,
  boxShadow: "0 2px 8px #eee",
  marginBottom: 16,
  padding: 0,
  height: 64,
  position: "relative",
  overflow: "hidden",
}));

const TabItem = styled(Box)(({ theme, active }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  justifyContent: "center",
  padding: "0 32px",
  height: "100%",
  position: "relative",
  cursor: "pointer",
  transition: "all 0.2s ease",
  // backgroundColor: active ? "#fff" : "#E6EBEF",
  // borderRadius: active ? "14px 14px 0 0" : "8px",
  margin: active ? "0 4px" : "0 2px",
  ...(active && {
    "&::after": {
      content: '""',
      position: "absolute",
      left: 0,
      right: 0,
      bottom: 0,
      height: 4,
      background: "#1889a5",
      borderRadius: 2,
    },
  }),
}));

function FlightResultsMain({
  searchResults = [],
  isSearching = false,
  totalResults = 0,
  tripType = "Round-trip",
}) {
  const { activeSort, setActiveSort, from, to, carryOnCount, checkedBagCount } = useSearchContext();
  const [sortedResults, setSortedResults] = useState([]);
  const [sortDropdownAnchor, setSortDropdownAnchor] = useState(null);
  const [hoveredTab, setHoveredTab] = useState(null);
  const [saveDrawerOpen, setSaveDrawerOpen] = useState(false);
  const [defaultTripName, setDefaultTripName] = useState("My Trip");

  // Helper: detect if current sort is one of the "Other sort" options
  const isOtherSort = (sortType) => {
    return [
      "earliest-takeoff-sfo",
      "latest-takeoff-sfo",
      "earliest-landing-lax",
      "latest-landing-lax",
      "highest-price",
      "slowest",
    ].includes(sortType);
  };

  // Helper: map selected other-sort into title + subtitle for the pill
  const getOtherSortDisplay = (sortType) => {
    switch (sortType) {
      case "earliest-takeoff-sfo":
        return {
          title: `Take-off (${fromAirportCode})`,
          subtitle: "Earliest to latest",
        };
      case "latest-takeoff-sfo":
        return {
          title: `Take-off (${fromAirportCode})`,
          subtitle: "Latest to earliest",
        };
      case "earliest-landing-lax":
        return {
          title: `Landing (${toAirportCode})`,
          subtitle: "Earliest to latest",
        };
      case "latest-landing-lax":
        return {
          title: `Landing (${toAirportCode})`,
          subtitle: "Latest to earliest",
        };
      case "highest-price":
        return { title: "Price", subtitle: "Highest to lowest" };
      case "slowest":
        return { title: "Duration", subtitle: "Longest to shortest" };
      default:
        return { title: "Other sort", subtitle: "" };
    }
  };

  // Extract airport codes from the from/to arrays
  const getAirportCode = (locationArray) => {
    if (locationArray && locationArray.length > 0) {
      const location = locationArray[0];
      const match = location.match(/\(([A-Z]{3})\)/);
      return match ? match[1] : location.split(" ").pop();
    }
    return "";
  };

  const getCityName = (locationArray) => {
    if (locationArray && locationArray.length > 0) {
      const location = locationArray[0];
      const idx = location.indexOf(" (");
      return idx >= 0 ? location.slice(0, idx) : location;
    }
    return "";
  };

  const fromAirportCode = getAirportCode(from);
  const toAirportCode = getAirportCode(to);
  const toCityName = getCityName(to);

  // Sort results whenever searchResults or activeSort changes
  useEffect(() => {
    if (searchResults && searchResults.length > 0) {
      const sorted = sortFlights(searchResults, activeSort);
      setSortedResults(sorted);
    } else {
      setSortedResults([]);
    }
  }, [searchResults, activeSort]);

  // Add a small delay to show sorting is happening
  const [isSorting, setIsSorting] = useState(false);

  useEffect(() => {
    if (searchResults && searchResults.length > 0) {
      setIsSorting(true);
      const timer = setTimeout(() => {
        setIsSorting(false);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [activeSort]);

  // Handle tab click
  const handleTabClick = (sortType) => {
    setActiveSort(sortType);
  };

  // Handle dropdown open/close
  const handleSortDropdownOpen = (event) => {
    setSortDropdownAnchor(event.currentTarget);
  };

  const handleSortDropdownClose = () => {
    setSortDropdownAnchor(null);
  };

  const handleSortOptionSelect = (sortType) => {
    setActiveSort(sortType);
    handleSortDropdownClose();
  };

  const handleOpenSave = () => {
    const name = toCityName ? `${toCityName} Trip` : "My Trip";
    setDefaultTripName(name);
    setSaveDrawerOpen(true);
  };

  // Calculate summary statistics
  const getSummaryStats = () => {
    if (!searchResults || searchResults.length === 0) {
      return { count: 0, minPrice: 0, avgDuration: "0h 0m" };
    }

    const prices = searchResults.map((result) => result.totalPrice);
    const minPrice = Math.min(...prices);
    const avgPrice =
      prices.reduce((sum, price) => sum + price, 0) / prices.length;

    // Calculate average duration
    const durations = searchResults.map((result) => {
      const durationStr = result.outbound.duration;
      const match = durationStr.match(/(\d+)h\s*(\d+)?m?/);
      if (match) {
        const hours = parseInt(match[1]);
        const minutes = match[2] ? parseInt(match[2]) : 0;
        return hours * 60 + minutes;
      }
      return 0;
    });

    const avgDurationMinutes =
      durations.reduce((sum, minutes) => sum + minutes, 0) / durations.length;
    const avgHours = Math.floor(avgDurationMinutes / 60);
    const avgMinutes = avgDurationMinutes % 60;
    const avgDuration = `${avgHours}h ${avgMinutes}m`;

    return {
      count: searchResults.length,
      minPrice: Math.round(minPrice),
      avgPrice: Math.round(avgPrice),
      avgDuration,
    };
  };

  const stats = getSummaryStats();

  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      {/* Tabs */}
      <TabContainer sx={{ borderRadius: 2, display: "flex", alignItems: "center", 
      position: "relative",
    backgroundColor: "#fff",
    margin: "0 auto 16px",
    height: 78,
    boxShadow: "0 3px 6px rgba(25, 32, 36, 0.16), 0 -1px 4px rgba(25, 32, 36, 0.04)",
        }}>
        <TabItem
          active={activeSort === "cheapest"}
          onClick={() => handleTabClick("cheapest")}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              borderRadius: 2,
              padding: "8px 12px",
              width: "100%",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: "#E6EBEF",
                borderRadius: 2,
              },
            }}
            onMouseEnter={() => setHoveredTab("cheapest")}
            onMouseLeave={() => setHoveredTab(null)}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                fontSize: 16,
                color: activeSort === "cheapest" ? "#5a6872" : "#666",
                marginBottom: 0.25,
              }}
            >
              Cheapest
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "#999", fontWeight: 400, fontSize: 12 }}
            >
              £{stats.minPrice} • {stats.avgDuration}
            </Typography>
          </Box>
        </TabItem>
        <Divider
          orientation="vertical"
          flexItem
          sx={{ height: 30, backgroundColor: "#e3e7ea", alignSelf: "center" }}
        />
        <TabItem
          active={activeSort === "best"}
          onClick={() => handleTabClick("best")}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              borderRadius: 2,
              padding: "8px 12px",
              width: "100%",
              transition: "all 0.2s ease",
              "&:hover": {  
                backgroundColor: "#E6EBEF",
                borderRadius: 2,
              },
            }}
            onMouseEnter={() => setHoveredTab("best")}
            onMouseLeave={() => setHoveredTab(null)}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                fontWeight: 700,
                fontSize: 16,
                color: activeSort === "best" ? "#000" : "#666",
                marginBottom: 0.25,
              }}
            >
              Best
              <InfoIcon sx={{ fontSize: 18, marginLeft: 0.5, color: "#666" }} />
            </Box>
            <Typography
              variant="body1"
              sx={{ color: "#999", fontWeight: 400, fontSize: 12 }}
            >
              £{stats.minPrice} • {stats.avgDuration}
            </Typography>
          </Box>
        </TabItem>
         <Divider
          orientation="vertical"
          flexItem
          sx={{ height: 30, backgroundColor: "#e3e7ea", alignSelf: "center" }}
        />

        <TabItem
          active={activeSort === "quickest"}
          onClick={() => handleTabClick("quickest")}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              borderRadius: 2,
              padding: "8px 12px",
              width: "100%",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: "#E6EBEF",
                borderRadius: 2,
              },
            }}
            onMouseEnter={() => setHoveredTab("quickest")}
            onMouseLeave={() => setHoveredTab(null)}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                fontSize: 16,
                color: activeSort === "quickest" ? "#000" : "#666",
                marginBottom: 0.25,
              }}
            >
              Quickest
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "#999", fontWeight: 400, fontSize: 12 }}
            >
              £{stats.minPrice} • {stats.avgDuration}
            </Typography>
          </Box>
        </TabItem>
         <Divider
          orientation="vertical"
          flexItem
          sx={{ height: 30, backgroundColor: "#e3e7ea", alignSelf: "center" }}
        />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            marginLeft: "auto",
            cursor: "pointer",
            margin: "0 2px",
            position: "relative",
            ...(isOtherSort(activeSort) && {
              "&::after": {
                content: '""',
                position: "absolute",
                left: 0,
                right: 0,
                bottom: 0,
                height: 4,
                background: "#1889a5",
                borderRadius: 2,
              },
            }),
          }}
          onClick={handleSortDropdownOpen}
        >
          {isOtherSort(activeSort) ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                borderRadius: 2,
                padding: "10px 14px",
                transition: "background-color 0.2s ease",
                "&:hover": { backgroundColor: "#E6EBEF" },
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#111", lineHeight: 1.1 }}>
                  {getOtherSortDisplay(activeSort).title}
                </Typography>
                <Typography sx={{ fontSize: 12, color: "#666" }}>
                  {getOtherSortDisplay(activeSort).subtitle}
                </Typography>
              </Box>
              <SortIcon sx={{ fontSize: 18, color: "#111" }} />
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                padding: "10px 14px",
                borderRadius: 2,
                transition: "all 0.2s ease",
                "&:hover": { backgroundColor: "#E6EBEF" },
              }}
            >
              <SortIcon sx={{ fontSize: 18, color: "#111" }} />
              <Typography sx={{ fontSize: 14, color: "#111", fontWeight: 400 }}>
                Other sort
              </Typography>
            </Box>
          )}
        </Box>

        {/* Sort Dropdown Menu */}
        <Menu
          anchorEl={sortDropdownAnchor}
          open={Boolean(sortDropdownAnchor)}
          onClose={handleSortDropdownClose}
          PaperProps={{
            sx: {
              mt: 1,
              minWidth: 280,
              maxHeight: 240,
              overflow: "auto",
              boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
              borderRadius: 2,
              "& .MuiMenuItem-root": {
                padding: "8px 16px",
                fontSize: 13,
                color: "#333",
                minHeight: "36px",
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                },
              },
            },
          }}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        >
          <MenuItem
            onClick={() => handleSortOptionSelect("earliest-takeoff-sfo")}
          >
            <ListItemText primary={`Earliest take-off (${fromAirportCode})`} />
          </MenuItem>
          <MenuItem
            onClick={() => handleSortOptionSelect("latest-takeoff-sfo")}
          >
            <ListItemText primary={`Latest take-off (${fromAirportCode})`} />
          </MenuItem>
          <MenuItem
            onClick={() => handleSortOptionSelect("earliest-landing-lax")}
          >
            <ListItemText primary={`Earliest landing (${toAirportCode})`} />
          </MenuItem>
          <MenuItem
            onClick={() => handleSortOptionSelect("latest-landing-lax")}
          >
            <ListItemText primary={`Latest landing (${toAirportCode})`} />
          </MenuItem>
          <MenuItem onClick={() => handleSortOptionSelect("highest-price")}>
            <ListItemText primary="Highest price" />
          </MenuItem>
          <MenuItem onClick={() => handleSortOptionSelect("slowest")}>
            <ListItemText primary="Slowest" />
          </MenuItem>
        </Menu>
      </TabContainer>

      {/* Sort-specific Ad Banner (hidden for Best, because Best shows inline header on first card) */}
      {activeSort !== "best" && <SortAdBanner activeSort={activeSort} />}

      {/* Loading State */}
      {isSearching && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 4,
          }}
        >
          <CircularProgress />
          <Typography sx={{ ml: 2, color: "#666" }}>
            Searching for flights...
          </Typography>
        </Box>
      )}

      {/* No Results */}
      {!isSearching && searchResults.length === 0 && totalResults === 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 4,
          }}
        >
          <Typography sx={{ color: "#666" }}>
            No flights found for your search criteria
          </Typography>
        </Box>
      )}

      {/* Filtered Out Results */}
      {!isSearching && searchResults.length === 0 && totalResults > 0 && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            py: 4,
          }}
        >
          <Typography
            sx={{ color: "#666", fontSize: 18, fontWeight: 500, mb: 1 }}
          >
            No flights match your current filters
          </Typography>
          <Typography sx={{ color: "#999", fontSize: 14 }}>
            Try adjusting your filters to see more results from {totalResults}{" "}
            available flights
          </Typography>
        </Box>
      )}

      {/* Flight Results List */}
      {!isSearching && sortedResults.length > 0 && (
        <Stack spacing={3}>
          {isSorting && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 1,
                color: "#666",
                fontSize: 14,
              }}
            >
              <CircularProgress size={16} sx={{ mr: 1 }} />
              Sorting by{" "}
              {activeSort === "cheapest"
                ? "price"
                : activeSort === "best"
                ? "quality"
                : "duration"}
              ...
            </Box>
          )}
          {sortedResults.map((result, index) => {
            const outbound = result.outbound;
            const returnFlight = result.returnFlights.length > 0 ? result.returnFlights[0] : null;

            // Calculate bag prices for the flight
            const outboundPriceCalculation = calculateBagPrices(outbound, carryOnCount, checkedBagCount);
            const returnPriceCalculation = returnFlight ? calculateBagPrices(returnFlight, carryOnCount, checkedBagCount) : null;

            // Detect international route using airport -> country mapping
            // Fall back to selected metro codes (e.g., NYC, LON) when specific airport isn't in cities.json
            const depCity = getCityByAirportCode(outbound?.departure?.airport || "");
            const arrCity = getCityByAirportCode(outbound?.arrival?.airport || "");
            const depInfo = depCity || getCityByAirportCode(fromAirportCode);
            const arrInfo = arrCity || getCityByAirportCode(toAirportCode);
            const isInternationalRoute = !!(
              depInfo &&
              arrInfo &&
              depInfo.country &&
              arrInfo.country &&
              depInfo.country !== arrInfo.country
            );
            
            // Use SingleRateCard for one-way trips, DoubleRateCard for round-trip
            if (tripType === "One-way") {
              if (isInternationalRoute) {
                return (
                  <InternationalRateCard
                    key={`${outbound.id}-${index}`}
                    showBestHeader={activeSort === "best" && index === 0}
                    airlineImg={outbound.airlineImg}
                    airlineName={outbound.airlineName}
                    flightType={outbound.stops === 0 ? "nonstop" : `${outbound.stops} stop`}
                    time={`${outbound.departure.time} – ${outbound.arrival.time}`}
                    duration={outbound.duration}
                    route={`${outbound.departure.airport}-${outbound.arrival.airport}`}
                    price={formatPrice(outboundPriceCalculation.totalPrice)}
                    onSelect={() => console.log("Select international one-way:", outbound)}
                    onSave={handleOpenSave}
                    onShare={() => console.log("Shared flight:", result)}
                  />
                );
              }
              return (
                <SingleRateCard
                  key={`${outbound.id}-${index}`}
                  showBestHeader={activeSort === "best" && index === 0}
                  airlineImg={outbound.airlineImg}
                  airlineName={outbound.airlineName}
                  flightType={
                    outbound.stops === 0 ? "nonstop" : `${outbound.stops} stop`
                  }
                  time={`${outbound.departure.time} – ${outbound.arrival.time}`}
                  duration={outbound.duration}
                  route={`${outbound.departure.airport}-${outbound.arrival.airport}`}
                  price1={formatPrice(outboundPriceCalculation.totalPrice)}
                  price2={formatPrice(outboundPriceCalculation.totalPrice + 30)} // Premium price
                  bagCost={outboundPriceCalculation.bagCosts.total}
                  hasBagIncrease={outboundPriceCalculation.totalPrice > outboundPriceCalculation.basePrice}
                  onSelect1={() => console.log("Selected outbound flight:", outbound)}
                  onSelect2={() => console.log("Selected premium flight:", outbound)}
                  onSave={handleOpenSave}
                  onShare={() => console.log("Shared flight:", result)}
                />
              );
            } else {
              // Round-trip flights: show International card if crossing countries, else DoubleRateCard
              if (isInternationalRoute) {
                return (
                  <InternationalRateCard
                    key={`${outbound.id}-${index}`}
                    showBestHeader={activeSort === "best" && index === 0}
                    airlineImg={outbound.airlineImg}
                    airlineName={outbound.airlineName}
                    flightType={outbound.stops === 0 ? "nonstop" : `${outbound.stops} stop`}
                    time={`${outbound.departure.time} – ${outbound.arrival.time}`}
                    duration={outbound.duration}
                    route={`${outbound.departure.airport}-${outbound.arrival.airport}`}
                    price={formatPrice(outboundPriceCalculation.totalPrice + (returnPriceCalculation ? returnPriceCalculation.totalPrice : 0))}
                    // Amenities from actual flight data
                    amenities={outbound.amenities || []}
                    returnAmenities={returnFlight ? returnFlight.amenities || [] : []}
                    // Return flight props
                    returnTime={
                      returnFlight ? `${returnFlight.departure.time} – ${returnFlight.arrival.time}` : null
                    }
                    returnRoute={
                      returnFlight ? `${returnFlight.departure.airport}-${returnFlight.arrival.airport}` : null
                    }
                    returnDuration={returnFlight ? returnFlight.duration : null}
                    returnFlightType={
                      returnFlight ? (returnFlight.stops === 0 ? "nonstop" : `${returnFlight.stops} stop`) : null
                    }
                    returnFlightNumber={returnFlight ? returnFlight.flightNumber : null}
                    returnAircraft={returnFlight ? returnFlight.aircraft : null}
                    onSelect={() => console.log("Select international round-trip:", result)}
                    onSave={handleOpenSave}
                    onShare={() => console.log("Shared flight:", result)}
                  />
                );
              }
                              return (
                  <DoubleRateCard
                    key={`${outbound.id}-${index}`}
                    showBestHeader={activeSort === "best" && index === 0}
                    airlineImg={outbound.airlineImg}
                    airlineName={outbound.airlineName}
                    flightType={
                      outbound.stops === 0 ? "nonstop" : `${outbound.stops} stop`
                    }
                    time={`${outbound.departure.time} – ${outbound.arrival.time}`}
                    duration={outbound.duration}
                    route={`${outbound.departure.airport}-${outbound.arrival.airport}`}
                    price1={formatPrice(outboundPriceCalculation.totalPrice)}
                    price2={returnFlight ? formatPrice(returnPriceCalculation.totalPrice) : "N/A"}
                    // Amenities from actual flight data
                    amenities={outbound.amenities || []}
                    returnAmenities={returnFlight ? returnFlight.amenities || [] : []}
                    // Return flight data
                    returnTime={
                      returnFlight
                        ? `${returnFlight.departure.time} – ${returnFlight.arrival.time}`
                        : null
                    }
                    returnRoute={
                      returnFlight
                        ? `${returnFlight.departure.airport}-${returnFlight.arrival.airport}`
                        : null
                    }
                    returnDuration={returnFlight ? returnFlight.duration : null}
                    returnFlightType={
                      returnFlight
                        ? returnFlight.stops === 0
                          ? "nonstop"
                          : `${returnFlight.stops} stop`
                        : null
                    }
                    returnFlightNumber={
                      returnFlight ? returnFlight.flightNumber : null
                    }
                    returnAircraft={returnFlight ? returnFlight.aircraft : null}
                    bagCost={outboundPriceCalculation.bagCosts.total + (returnPriceCalculation ? returnPriceCalculation.bagCosts.total : 0)}
                    hasBagIncrease={outboundPriceCalculation.totalPrice > outboundPriceCalculation.basePrice || (returnPriceCalculation && returnPriceCalculation.totalPrice > returnPriceCalculation.basePrice)}
                    onSelect1={() => console.log("Selected outbound flight:", outbound)}
                    onSelect2={() => console.log("Selected return flight:", returnFlight)}
                    onSave={handleOpenSave}
                    onShare={() => console.log("Shared flight:", result)}
                  />
                );
            }
          })}
        </Stack>
      )}

      <SaveTripDrawer
        open={saveDrawerOpen}
        onClose={() => setSaveDrawerOpen(false)}
        defaultTripName={defaultTripName}
        imageUrl="/get.jpg"
        headerHeight={64}
        onCreate={(name) => {
          console.log("Trip created:", name);
          setSaveDrawerOpen(false);
        }}
      />
    </Box>
  );
}

export default FlightResultsMain;
